import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { PageHeader, Spinner, Button, subtleNeo } from '@brainsway/ui';
import { HospitalForm } from '../components/HospitalForm';
import { useHospitalDetail, useUpdateHospital } from '../hooks';
import type { HospitalFormData } from '../types';

export function EditHospitalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: hospital, isLoading } = useHospitalDetail(id);
  const updateMutation = useUpdateHospital();

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!hospital) {
    return (
      <div className="py-20 text-center text-neutral-500">
        {t('common.notFound', 'Not found')}
      </div>
    );
  }

  const handleSubmit = (data: HospitalFormData) => {
    updateMutation.mutate(
      { id: id!, data },
      {
        onSuccess: () => {
          toast.success(t('hospitals.updated', 'Hospital updated'));
          navigate(`/admin/hospitals/${id}`);
        },
        onError: (err: any) => {
          const message = err.response?.data?.message || t('common.error');
          toast.error(message);
        },
      },
    );
  };

  return (
    <motion.div initial="hidden" animate="visible" variants={subtleNeo.fadeIn} className="mx-auto max-w-2xl space-y-5">
      <PageHeader
        title={t('hospitals.editHospital', 'Edit Hospital')}
        backButton={
          <Link to="/admin/hospitals">
            <Button variant="ghost" size="icon"><ArrowRight className="h-5 w-5 rtl:rotate-180" /></Button>
          </Link>
        }
      />
      <HospitalForm
        existingHospital={hospital}
        onSubmit={handleSubmit}
        isLoading={updateMutation.isPending}
      />
    </motion.div>
  );
}
